import { get, map, uniq, includes, some, filter, isArray } from 'lodash';
import { getUserSignDocsAPI } from '../../Services/api';
import { getAllSignDocTypes } from '../../Components/SignDoc/SignDocLib';
import { fetchAdminCreatedSignDocTypes } from '../../Components/Hooks/useQueryHooks';


/**
 * Gets all SignDoc types that the user has tasks for
 * @param {object} params - Function parameters
 * @param {Array<string>} params.categories - Array of categories to filter by
 * @param {boolean} params.showForceCompletion - Whether to include force completion tasks
 * @param {boolean} params.showNonForceCompletion - Whether to include non-force completion tasks
 * @returns {Promise<Array<string>>} Array of SignDoc types the user has tasks for
 */
export const getUserTaskTypes = async ({ categories, showForceCompletion = true, showNonForceCompletion = false }) => {
    try {
        const userSignDocs = await getUserSignDocsAPI({
            types: getAllSignDocTypes(), // Get all types
            categories,
            showForceCompletion,
            showNonForceCompletion,
            hideLearningPathItems: true
        });
        if (!userSignDocs || userSignDocs.length === 0) {
            return [];
        }
        
        // The API already filters by showForceCompletion and showNonForceCompletion
        // So we don't need to filter again here, just extract the types
        const userTaskTypes = uniq(map(userSignDocs, (userSignDoc) => 
            get(userSignDoc, 'signDoc.type')
        ));
        
        return userTaskTypes.filter(type => type); // Filter out null/undefined
    } catch (error) {
        console.error('Error getting user task types:', error);
        return [];
    }
};

/**
 * Gets all SignDoc types that the admin has created.
 * Uses the lightweight createdTypes endpoint (server-side aggregate) instead of
 * fetching the full admin SignDoc list.
 * @param {object} params - Function parameters
 * @param {Array<string>} params.categories - Array of categories to filter by
 * @param {boolean} params.isForReportsComponent - When true, only types with at least one valid organization assignment are returned
 * @param {boolean} [params.showArchived=false] - When true, returns types among ARCHIVED documents (archive tab)
 * @returns {Promise<Array<string>>} Array of SignDoc types the admin has created
 */
export const getAdminCreatedTypes = async ({ categories, isForReportsComponent = false, showArchived = false }) => {
    try {
        // Cached fetch of distinct {type, category, hasValidAssignment} entries
        const createdTypeEntries = await fetchAdminCreatedSignDocTypes({showArchived});

        if (!createdTypeEntries || createdTypeEntries.length === 0) {
            return [];
        }

        const relevantEntries = filter(createdTypeEntries, entry => {
            if (categories && categories.length > 0 && !includes(categories, get(entry, 'category'))) {
                return false;
            }
            if (isForReportsComponent && !get(entry, 'hasValidAssignment')) {
                // Reports only show types that are currently assigned somewhere
                return false;
            }
            return true;
        });

        const adminCreatedTypes = filter(
            uniq(map(relevantEntries, entry => get(entry, 'type'))),
            type => type
        );

        return adminCreatedTypes;
    } catch (error) {
        console.error('Error getting admin created types:', error);
        return [];
    }
};

/**
 * Checks if a tab should be shown based on user tasks
 * @param {object} params - Function parameters
 * @param {Array<string>} params.tabTypes - Array of SignDoc types for this tab
 * @param {Array<string>} params.existingTypes - Array of types the user has tasks for
 * @returns {boolean} True if the tab should be shown
 */
export const shouldShowTab = ({ tabTypes, existingTypes }) => {
    if (!tabTypes || !existingTypes) return false;

    // Check if user has tasks for any of the tab's types
    return some(tabTypes, type => includes(existingTypes, type));
};

/**
 * Filters tabs based on TabsTypes mapping and existing types
 * Used in both Learning Management and Learning Path editor
 * @param {object} params - Function parameters
 * @param {Array<object>} params.tabs - Array of tab objects with value property
 * @param {object} params.tabsTypes - Mapping of tab values to SignDoc types (e.g., TabsTypes)
 * @param {Array<string>} params.existingTypes - Array of existing SignDoc types
 * @returns {Array<object>} Filtered array of tabs
 */
export const filterTabsByExistingTypes = ({ tabs, tabsTypes, existingTypes }) => {
    if (!tabs || !tabsTypes || !existingTypes) return tabs || [];

    return tabs.filter(tab => {
        const tabValue = tab.value;
        const tabTypes = tabsTypes[tabValue];

        // If no types defined for this tab, show it by default
        if (!tabTypes) return true;

        // Check if any of the tab's types exist in existingTypes
        return shouldShowTab({ tabTypes, existingTypes });
    });
};